<script>
import { defineComponent } from 'vue';
import AntInput from '@/components/AntInput.vue';
import InputRulesMixin from '@/Mixins/InputRulesMixin';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'DhmeManualImportCreateObjectDialog',
  components: { AntInput },
  mixins: [InputRulesMixin],
  props: {
    displayed: {
      type: Boolean,
      required: true,
    },
    category: {
      type: String,
      required: false,
      default: null,
    },
    object: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data: () => {
    return {
      createObjectItem: {},
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      objectRegister: 'dhmeManualImportStore/objectsRegister',
      elements: 'dhmeManualImportStore/elements',
    }),
    showDialog() {
      return this.displayed;
    },
    autocompleteDescriptionItems() {
      return this.objectRegister
        .map((item) => item.name)
        .sort((a, b) => a.localeCompare(b));
    },
    autocompleteSteelQualityItems() {
      return this.objectRegister.map((item) => item.steel_quality);
    },
    elementCategories() {
      return this.elements
        .map((item) => item.element_category)
        .sort((a, b) => a.localeCompare(b));
    },
  },
  watch: {
    showDialog(value) {
      if (value) {
        this.createObjectItem = this.object ? { ...this.object } : {};
        if (this.category) {
          this.createObjectItem.category = this.category;
        }
      } else {
        this.createObjectItem = {};
      }
    },
  },

  methods: {
    closeDialog() {
      this.createObjectItem = {}; // reset data
      this.$nextTick(() => {
        if (this.$refs['create-object-form']) {
          this.$refs['create-object-form'].reset(); // reset form
        }
        this.$emit('closeDialog');
      });
    },
    async createObject() {
      // Programmatically get the current input values from the comboboxes
      const nameInput = this.$refs.nameCombobox.$el.querySelector('input');
      const steelQualityInput =
        this.$refs.steelQualityCombobox.$el.querySelector('input');

      if (nameInput) {
        this.createObjectItem.name = nameInput.value;
      }
      if (steelQualityInput) {
        this.createObjectItem.steel_quality = steelQualityInput.value;
      }

      // Validate the form
      if (this.$refs['create-object-form'].validate()) {
        this.isLoading = true;
        try {
          if (this.createObjectItem.id) {
            // Update existing record
            await this.$store.dispatch(
              'dhmeManualImportStore/manualImportUpdateObjectRecord',
              {
                recordId: this.createObjectItem.id,
                recordBody: this.createObjectItem,
              }
            );
          } else {
            // Create new record
            await this.$store.dispatch(
              'dhmeManualImportStore/manualImportCreateObjectRecord',
              {
                recordBody: { ...this.createObjectItem },
              }
            );
          }

          // Add the new name dynamically to the list if it's not already present
          if (
            !this.autocompleteDescriptionItems.includes(
              this.createObjectItem.name
            )
          ) {
            this.objectRegister.push({ name: this.createObjectItem.name });
          }

          // Add the new steel_quality dynamically to the list if it's not already present
          if (
            this.createObjectItem.steel_quality &&
            !this.autocompleteSteelQualityItems.includes(
              this.createObjectItem.steel_quality
            )
          ) {
            this.objectRegister.push({
              steel_quality: this.createObjectItem.steel_quality,
            });
          }
        } catch (error) {
          console.error('Error creating/updating object:', error);
          this.$store.commit('showNotification', {
            content: 'Failed to create or update object. Please try again.',
            color: 'error',
          });
        } finally {
          this.isLoading = false;
          this.closeDialog();
        }
      }
    },
  },
});
</script>

<template>
  <v-dialog
    v-model="showDialog"
    max-width="600"
    persistent
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <v-card-title class="justify-center text-uppercase headline">
        Create object type
      </v-card-title>
      <v-divider />
      <div class="px-10 pb-5">
        <v-form ref="create-object-form">
          <div class="d-flex">
            <ant-input label="Code">
              <template #input-field>
                <v-text-field
                  v-model="createObjectItem.object_type"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="Code"
                  single-line
                />
              </template>
            </ant-input>

            <ant-input class="ml-2 flex-grow-1" label="Description">
              <template #input-field>
                <v-combobox
                  ref="nameCombobox"
                  v-model="createObjectItem.name"
                  :items="autocompleteDescriptionItems"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="Description"
                  single-line
                />
              </template>
            </ant-input>
          </div>

          <div class="d-flex">
            <ant-input is-optional label="Length">
              <template #input-field>
                <v-text-field
                  v-model="createObjectItem.length"
                  dense
                  filled
                  hide-details
                  placeholder="Length"
                  single-line
                  suffix="mm"
                />
              </template>
            </ant-input>
            <ant-input class="mx-2" is-optional label="Width">
              <template #input-field>
                <v-text-field
                  v-model="createObjectItem.width"
                  dense
                  filled
                  hide-details
                  placeholder="Width"
                  single-line
                  suffix="mm"
                />
              </template>
            </ant-input>
            <ant-input is-optional label="Height">
              <template #input-field>
                <v-text-field
                  v-model="createObjectItem.height"
                  dense
                  filled
                  hide-details
                  placeholder="height"
                  single-line
                  suffix="mm"
                />
              </template>
            </ant-input>
          </div>
          <div class="d-flex">
            <ant-input class="mr-2 flex-grow-1" label="Category">
              <template #input-field>
                <v-combobox
                  v-model="createObjectItem.category"
                  :items="elementCategories"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="category"
                  single-line
                />
              </template>
            </ant-input>
            <ant-input is-optional label="Steel quality">
              <template #input-field>
                <v-combobox
                  ref="steelQualityCombobox"
                  v-model="createObjectItem.steel_quality"
                  :items="autocompleteSteelQualityItems"
                  dense
                  filled
                  hide-details
                  placeholder="Quality lvl"
                  single-line
                />
              </template>
            </ant-input>
          </div>
        </v-form>
      </div>
      <v-card-actions class="ant-dialog-actions-bg ant-border-top">
        <v-spacer />
        <v-btn color="error" small text @click="closeDialog"> Cancel</v-btn>
        <v-btn
          :loading="isLoading"
          color="primary"
          elevation="0"
          small
          @click="createObject"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped></style>
